import {RecipeComponentType} from '@domain/shared'
import {
  AssetSnapshot,
  AssetType,
  ProducingOperationModeSnapshot,
  Status
} from '@hconnect/common/types'

import {Recipe, RecipeComponent} from '../types'

import {getOperationModeIdToAssetIdMap, guardProducingMode} from './assets'

interface GetOptimizedMaterialIds {
  assets: AssetSnapshot[]
  recipesById: Record<string, Recipe>
}
/** function to get unique materialIds for all optimized assets */
export const getOptimizedMaterialIds = ({
  assets,
  recipesById
}: GetOptimizedMaterialIds): number[] => {
  const optimizedAssets = assets.filter((asset) => asset.isOptimized)
  const optimizedProductionModes = optimizedAssets
    .flatMap((asset) => asset.operationModes)
    .filter(guardProducingMode)

  const uniqueMaterialIdsInAllRecipeComponents = new Set(
    optimizedProductionModes
      // filtering out operation modes without current recipes, in case of data corruption
      .filter((operationMode) => recipesById[operationMode.recipeId] !== undefined)
      .flatMap(({recipeId}) => recipesById[recipeId].components)
      // filtering outputs
      .filter((component) => component.type === RecipeComponentType.Output)
      .map((component) => component.material.id)
  )
  return Array.from(uniqueMaterialIdsInAllRecipeComponents.values())
}

export const getRecipeComponentsWithMaterial = (
  materialId: number,
  recipe: Recipe,
  componentTypes: RecipeComponentType[]
): RecipeComponent[] => {
  return recipe.components
    .filter((component) => componentTypes.includes(component.type))
    .filter((component) => component.material.id === materialId)
}

interface GetMillProductionTimeForMaterial {
  assetsById: Record<string, AssetSnapshot>
  assets: AssetSnapshot[]
  recipesById: Record<string, Recipe>
  materialId: number
  tonsOfMaterial: number
}

export interface MillProductionTime {
  asset_name: string
  time: number
}

export const getMillProductionTimeForMaterial = ({
  assetsById,
  assets,
  recipesById,
  materialId,
  tonsOfMaterial
}: GetMillProductionTimeForMaterial): MillProductionTime[] => {
  const cementOrRawMillAssets = assets.filter((asset) =>
    [AssetType.RawMill, AssetType.CementMill].includes(asset.type)
  )
  const operationIdToAssetIdMap = getOperationModeIdToAssetIdMap(cementOrRawMillAssets)
  const allAssetOperationModesInAssets = cementOrRawMillAssets.flatMap((asset) =>
    asset.operationModes
      .filter(guardProducingMode)
      // filtering out deleted operation modes without current recipes
      .filter(
        (operationMode) =>
          operationMode.status !== Status.Deleted && !!recipesById[operationMode.recipeId]
      )
  )
  const operationModesUsingMaterial = allAssetOperationModesInAssets.filter(
    (operationMode) =>
      getRecipeComponentsWithMaterial(materialId, recipesById[operationMode.recipeId], [
        RecipeComponentType.Output
      ]).length > 0
  )

  const aomWithHighestThroughput = filterOperationModesByMaxThroughputPerAsset(
    operationModesUsingMaterial,
    operationIdToAssetIdMap
  )

  return aomWithHighestThroughput.map((aom) => ({
    asset_name: assetsById[operationIdToAssetIdMap[aom.id]].name,
    time: tonsOfMaterial / aom.throughput
  }))
}

export const filterOperationModesByMaxThroughputPerAsset = (
  operationModes: ProducingOperationModeSnapshot[],
  operationIdToAssetIdMap: Record<string, number>
): ProducingOperationModeSnapshot[] => {
  const assetToMaxThroughputAom: Record<string, ProducingOperationModeSnapshot> = {}
  for (const operationMode of operationModes) {
    const assetId = operationIdToAssetIdMap[operationMode.id]
    const maxThroughput = assetToMaxThroughputAom[assetId]?.throughput
    if (maxThroughput === undefined || maxThroughput < operationMode.throughput) {
      assetToMaxThroughputAom[assetId] = operationMode
    }
  }
  return Object.values(assetToMaxThroughputAom)
}
